import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardClass = "bg-white rounded-xl shadow-sm border border-gray-200";

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const PayrollPage = ({ showToast }) => {
  const toast = showToast || ((msg) => console.log(msg));

  const [view, setView] = useState('flat'); // 'flat' | 'grouped'
  const [defaultRange] = useState(() => {
    const end = moment();
    const start = moment().subtract(13, 'days');
    return { from: start.format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') };
  });
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [memberFilter, setMemberFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('no'); // default to unpaid
  const [search, setSearch] = useState('');

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [payRuns, setPayRuns] = useState([]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const [showPayStub, setShowPayStub] = useState(false);
  const [stubMemberId, setStubMemberId] = useState('');
  const [stubDateFrom, setStubDateFrom] = useState(defaultRange.from);
  const [stubDateTo, setStubDateTo] = useState(defaultRange.to);
  const [stubPaidFilter, setStubPaidFilter] = useState('');
  const [payRunNote, setPayRunNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (memberFilter) params.append('team_member_id', memberFilter);
      if (paidFilter) params.append('paid', paidFilter);
      const res = await axios.get(`${API}/payroll/payouts?${params.toString()}`);
      setPayouts(Array.isArray(res.data) ? res.data : []);
      setSelectedIds(new Set());
    } catch (err) {
      toast('Failed to load payouts', 'error');
    }
    setLoading(false);
  }, [dateFrom, dateTo, memberFilter, paidFilter, toast]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/team-members`);
      setTeamMembers(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { /* noop */ }
  }, []);

  const fetchDefaults = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll/defaults`);
      setDefaults(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { /* noop */ }
  }, []);

  const fetchPayRuns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll/pay-runs`);
      setPayRuns(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);
  useEffect(() => { fetchTeamMembers(); fetchDefaults(); fetchPayRuns(); }, [fetchTeamMembers, fetchDefaults, fetchPayRuns]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return payouts;
    return payouts.filter(p =>
      (p.client_name || '').toLowerCase().includes(q) ||
      (p.property_address || '').toLowerCase().includes(q) ||
      (p.product_name || '').toLowerCase().includes(q) ||
      (p.team_member_name || '').toLowerCase().includes(q)
    );
  }, [payouts, search]);

  const totals = useMemo(() => {
    const t = filtered.reduce((acc, p) => {
      acc.count += 1;
      acc.amount += Number(p.amount || 0);
      if (p.paid) { acc.paid += Number(p.amount || 0); acc.paidCount += 1; }
      else { acc.unpaid += Number(p.amount || 0); acc.unpaidCount += 1; }
      return acc;
    }, { count: 0, amount: 0, paid: 0, unpaid: 0, paidCount: 0, unpaidCount: 0 });
    return t;
  }, [filtered]);

  const selectedTotal = useMemo(() => {
    let amt = 0;
    filtered.forEach(p => { if (selectedIds.has(p.id)) amt += Number(p.amount || 0); });
    return amt;
  }, [filtered, selectedIds]);

  const selectableIds = useMemo(() => filtered.filter(p => !p.paid).map(p => p.id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreatePayRun = async () => {
    if (selectedIds.size === 0) {
      toast('Select at least one unpaid payout', 'info');
      return;
    }
    setProcessing(true);
    try {
      const lines = filtered
        .filter(p => selectedIds.has(p.id) && !p.paid)
        .map(p => ({ appointment_id: p.appointment_id, product_id: p.product_id, team_member_id: p.team_member_id, tier_key: p.tier_key || null, amount: p.amount }));
      const res = await axios.post(`${API}/payroll/pay-runs`, { lines, note: payRunNote });
      toast(`Pay run created: ${res.data.line_count} line(s), ${formatMoney(res.data.total_amount)}`, 'success');
      setPayRunNote('');
      await Promise.all([fetchPayouts(), fetchPayRuns()]);
    } catch (err) {
      toast('Failed to create pay run', 'error');
    }
    setProcessing(false);
  };

  const handleReverseRun = async (runId) => {
    if (!window.confirm('Reverse this pay run? All its payouts will be marked unpaid.')) return;
    try {
      await axios.delete(`${API}/payroll/pay-runs/${runId}`);
      toast('Pay run reversed', 'success');
      await Promise.all([fetchPayouts(), fetchPayRuns()]);
    } catch (err) {
      toast('Failed to reverse pay run', 'error');
    }
  };

  const handleDefaultChange = async (productId, amount, tierKey) => {
    try {
      await axios.put(`${API}/payroll/defaults/${productId}`, { amount, tier_key: tierKey });
      setDefaults(prev => prev.map(d => {
        if (d.product_id !== productId) return d;
        if (tierKey == null) return { ...d, amount: Number(amount || 0) };
        return { ...d, tiers: (d.tiers || []).map(t => t.tier_key === tierKey ? { ...t, amount: Number(amount || 0) } : t) };
      }));
    } catch (err) {
      toast('Failed to save default', 'error');
    }
  };

  const handleGeneratePayStub = async (mode) => {
    if (!stubMemberId) { toast('Choose a team member', 'info'); return; }
    try {
      const params = new URLSearchParams();
      params.append('team_member_id', stubMemberId);
      if (stubDateFrom) params.append('date_from', stubDateFrom);
      if (stubDateTo) params.append('date_to', stubDateTo);
      if (stubPaidFilter) params.append('paid', stubPaidFilter);
      const res = await axios.get(`${API}/payroll/pay-stub?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (mode === 'download') {
        const member = teamMembers.find(m => m.id === stubMemberId);
        const safe = (member?.name || 'TeamMember').replace(/\s+/g, '_');
        const link = document.createElement('a');
        link.href = url;
        link.download = `PayStub_${safe}_${moment().format('YYYYMMDD_HHmm')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast('Pay stub downloaded', 'success');
      } else {
        window.open(url, '_blank');
      }
      // Cleanup after a tick to ensure browser had time to open it
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast('Failed to generate pay stub', 'error');
    }
  };


  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(p => {
      if (!map.has(p.team_member_id)) map.set(p.team_member_id, { name: p.team_member_name, lines: [], total: 0, unpaid: 0, paid: 0 });
      const entry = map.get(p.team_member_id);
      entry.lines.push(p);
      entry.total += Number(p.amount || 0);
      if (p.paid) entry.paid += Number(p.amount || 0); else entry.unpaid += Number(p.amount || 0);
    });
    return [...map.values()].sort((a, b) => b.unpaid - a.unpaid);
  }, [filtered]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pb-8" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-2xl font-bold">Payroll</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPayStub(true)}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
            data-testid="open-pay-stub-btn"
          >
            Pay Stub
          </button>
          <button
            onClick={() => setShowRuns(true)}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
            data-testid="show-pay-runs-btn"
          >
            Pay Run History
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
            data-testid="open-payroll-settings-btn"
          >
            Default Payouts
          </button>
        </div>
      </div>

      {/* Summary Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Lines (visible)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.count}</p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatMoney(totals.amount)}</p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Unpaid</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{formatMoney(totals.unpaid)}</p>
          <p className="text-xs text-gray-500 mt-1">{totals.unpaidCount} line{totals.unpaidCount === 1 ? '' : 's'}</p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Paid</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{formatMoney(totals.paid)}</p>
          <p className="text-xs text-gray-500 mt-1">{totals.paidCount} line{totals.paidCount === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* Filters & view toggle */}
      <div className={`${cardClass} p-4`} style={{ backgroundColor: '#EFF6FF' }}>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="payroll-date-from" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="payroll-date-to" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Team Member</label>
            <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="payroll-member-filter">
              <option value="">All</option>
              {teamMembers.map(m => (<option key={m.id} value={m.id}>{m.name || m.email}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="payroll-status-filter">
              <option value="">All</option>
              <option value="no">Unpaid</option>
              <option value="yes">Paid</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search (client/address/product/team member)</label>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="payroll-search" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex bg-white rounded-lg border border-gray-200 p-0.5">
            <button onClick={() => setView('flat')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'flat' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="view-toggle-flat">Flat table</button>
            <button onClick={() => setView('grouped')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'grouped' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="view-toggle-grouped">Grouped by member</button>
          </div>
          <button onClick={() => { setDateFrom(defaultRange.from); setDateTo(defaultRange.to); setMemberFilter(''); setPaidFilter('no'); setSearch(''); }} className="text-xs text-gray-600 hover:text-gray-800" data-testid="reset-filters-btn">Reset filters</button>
        </div>
      </div>

      {/* Pay Run Action Bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-10 bg-blue-600 text-white rounded-xl shadow-lg p-3 flex flex-wrap items-center gap-3" data-testid="pay-run-action-bar">
          <span className="font-semibold">{selectedIds.size} selected</span>
          <span>•</span>
          <span>{formatMoney(selectedTotal)}</span>
          <input value={payRunNote} onChange={(e) => setPayRunNote(e.target.value)} placeholder="Add a note (optional)" className="flex-1 min-w-[160px] px-2 py-1 text-sm text-gray-900 rounded" data-testid="pay-run-note" />
          <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 rounded">Clear</button>
          <button onClick={handleCreatePayRun} disabled={processing} className="px-4 py-1.5 text-sm font-medium bg-white text-blue-700 hover:bg-blue-50 rounded disabled:opacity-60" data-testid="create-pay-run-btn">
            {processing ? 'Processing…' : `Pay ${selectedIds.size} line${selectedIds.size === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className={`${cardClass} p-8 text-center text-gray-500`}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className={`${cardClass} p-12 text-center text-gray-500`} data-testid="payroll-empty-state">
          <p className="text-base font-medium text-gray-700">No payouts in this range</p>
          <p className="text-sm mt-1">Try widening the date range or adjusting filters.</p>
        </div>
      ) : view === 'flat' ? (
        <FlatTable lines={filtered} selectedIds={selectedIds} toggleOne={toggleOne} toggleAll={toggleAll} allSelected={allSelected} hasSelectables={selectableIds.length > 0} />
      ) : (
        <GroupedView groups={grouped} selectedIds={selectedIds} toggleOne={toggleOne} />
      )}

      {/* Settings Drawer */}
      {showSettings && (
        <Drawer title="Default Payouts" onClose={() => setShowSettings(false)} testId="payroll-settings-drawer">
          <p className="text-sm text-gray-600 mb-3">Set the default payout amount paid to a team member for each product they perform. If a product has square-footage tiers, set a per-tier amount. The base amount is used when no tier matches. Overrides per team member can be set in Team &gt; Edit Member &gt; Payroll tab.</p>
          <div className="space-y-1">
            {defaults.length === 0 && (
              <p className="text-sm text-gray-500">No products yet — add products first.</p>
            )}
            {defaults.map(d => {
              const typeBadge = d.entity_type === 'addon'
                ? <span className="ml-2 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">Add-on</span>
                : d.entity_type === 'package'
                ? <span className="ml-2 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Package</span>
                : null;
              return (
              <div key={d.product_id} className="py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {d.product_name}{typeBadge}
                    </p>
                    <p className="text-xs text-gray-500">
                      {d.product_price > 0 ? `Sells for ${formatMoney(d.product_price)}` : d.entity_type === 'package' ? 'Bundle of products' : 'Add-on'}
                      {(d.tiers || []).length > 0 && ' • Base (no tier match)'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={d.amount}
                      onBlur={(e) => handleDefaultChange(d.product_id, parseFloat(e.target.value || 0), null)}
                      className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right"
                      data-testid={`default-amount-${d.product_id}`}
                    />
                  </div>
                </div>
                {(d.tiers || []).map(t => (
                  <div key={t.tier_key} className="flex items-center justify-between gap-3 pl-6 py-1 mt-1">
                    <div className="min-w-0 flex items-center gap-2">
                      <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      <span className="text-sm text-gray-700">{t.min_sqft}-{t.max_sqft} sqft</span>
                      <span className="text-xs text-gray-400">• Sells for {formatMoney(t.price)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={t.amount}
                        onBlur={(e) => handleDefaultChange(d.product_id, parseFloat(e.target.value || 0), t.tier_key)}
                        className="w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-right"
                        data-testid={`default-amount-${d.product_id}-${t.tier_key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              );
            })}
          </div>
        </Drawer>
      )}

      {/* Pay Runs Drawer */}
      {showRuns && (
        <Drawer title="Pay Run History" onClose={() => setShowRuns(false)} testId="pay-runs-drawer">
          {payRuns.length === 0 ? (
            <p className="text-sm text-gray-500">No pay runs yet.</p>
          ) : (
            <div className="space-y-2">
              {payRuns.map(r => (
                <PayRunRow
                  key={r.id}
                  run={r}
                  api={API}
                  onReverse={() => handleReverseRun(r.id)}
                  toast={toast}
                />
              ))}
            </div>
          )}
        </Drawer>
      )}

      {/* Pay Stub Drawer */}
      {showPayStub && (
        <Drawer title="Generate Pay Stub" onClose={() => setShowPayStub(false)} testId="pay-stub-drawer">
          <p className="text-sm text-gray-600 mb-4">Generate a printable PDF pay stub for a specific team member covering a date range. Includes every shoot with date / address / line items + grand total.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Team Member <span className="text-red-500">*</span></label>
              <select value={stubMemberId} onChange={(e) => setStubMemberId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="paystub-member">
                <option value="">Select a team member…</option>
                {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                <input type="date" value={stubDateFrom} onChange={(e) => setStubDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="paystub-date-from" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                <input type="date" value={stubDateTo} onChange={(e) => setStubDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="paystub-date-to" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Include</label>
              <select value={stubPaidFilter} onChange={(e) => setStubPaidFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="paystub-status">
                <option value="">All lines (paid + unpaid)</option>
                <option value="yes">Paid only</option>
                <option value="no">Unpaid only</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={() => handleGeneratePayStub('preview')}
                disabled={!stubMemberId}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                data-testid="paystub-preview-btn"
              >
                Preview in new tab
              </button>
              <button
                onClick={() => handleGeneratePayStub('download')}
                disabled={!stubMemberId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                data-testid="paystub-download-btn"
              >
                Download PDF
              </button>
            </div>
          </div>
        </Drawer>
      )}
    </div>
  );
};

const FlatTable = ({ lines, selectedIds, toggleOne, toggleAll, allSelected, hasSelectables }) => (
  <div className={`${cardClass} overflow-hidden`}>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
          <tr>
            <th className="px-3 py-2 text-left w-10">
              {hasSelectables && (
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-payouts" />
              )}
            </th>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Client</th>
            <th className="px-3 py-2 text-left">Address</th>
            <th className="px-3 py-2 text-left">Product</th>
            <th className="px-3 py-2 text-left">Team Member</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.map(p => (
            <tr key={p.id} className={`border-t border-gray-100 ${selectedIds.has(p.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
              <td className="px-3 py-2">
                {!p.paid && (
                  <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleOne(p.id)} data-testid={`select-payout-${p.id}`} />
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-gray-700">{p.scheduled_date ? moment(p.scheduled_date).format('MMM D, YYYY') : '—'}</td>
              <td className="px-3 py-2 text-gray-900">{p.client_name || '—'}</td>
              <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{p.property_address || '—'}</td>
              <td className="px-3 py-2 text-gray-700">
                <div>{p.product_name}</div>
                {p.tier_label && <div className="text-xs text-gray-500">{p.tier_label}</div>}
              </td>
              <td className="px-3 py-2 text-gray-700">{p.team_member_name}</td>
              <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatMoney(p.amount)}</td>
              <td className="px-3 py-2 text-center">
                {p.paid ? (
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-100 text-green-700">PAID</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-700">UNPAID</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const GroupedView = ({ groups, selectedIds, toggleOne }) => (
  <div className="space-y-3">
    {groups.map(g => (
      <details key={g.name} className={`${cardClass} overflow-hidden`} open>
        <summary className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 list-none">
          <div className="flex items-center gap-3">
            <svg className="w-4 h-4 text-gray-400 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <span className="font-semibold text-gray-900">{g.name}</span>
            <span className="text-xs text-gray-500">{g.lines.length} line{g.lines.length === 1 ? '' : 's'}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-orange-600 font-medium">Unpaid {formatMoney(g.unpaid)}</span>
            <span className="text-xs text-green-600 font-medium">Paid {formatMoney(g.paid)}</span>
            <span className="text-sm font-bold text-gray-900">{formatMoney(g.total)}</span>
          </div>
        </summary>
        <div className="border-t border-gray-100">
          <table className="min-w-full text-sm">
            <tbody>
              {g.lines.map(p => (
                <tr key={p.id} className={`border-t border-gray-100 first:border-0 ${selectedIds.has(p.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2 w-10">
                    {!p.paid && (
                      <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleOne(p.id)} data-testid={`grouped-select-${p.id}`} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{p.scheduled_date ? moment(p.scheduled_date).format('MMM D') : '—'}</td>
                  <td className="px-3 py-2 text-gray-900">{p.client_name}</td>
                  <td className="px-3 py-2 text-gray-600">
                    <div>{p.product_name}</div>
                    {p.tier_label && <div className="text-[10px] text-gray-500">{p.tier_label}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{formatMoney(p.amount)}</td>
                  <td className="px-3 py-2 text-center w-20">
                    {p.paid ? (
                      <span className="text-[10px] font-semibold text-green-700">PAID</span>
                    ) : (
                      <span className="text-[10px] font-semibold text-orange-600">UNPAID</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    ))}
  </div>
);

const PayRunRow = ({ run, api, onReverse, toast }) => {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  const refreshMembers = async () => {
    try {
      const res = await axios.get(`${api}/payroll/pay-runs/${run.id}/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (_e) { /* noop */ }
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && members === null) {
      setLoading(true);
      try {
        const res = await axios.get(`${api}/payroll/pay-runs/${run.id}/members`);
        setMembers(Array.isArray(res.data) ? res.data : []);
      } catch (_e) {
        toast('Failed to load members', 'error');
        setMembers([]);
      }
      setLoading(false);
    }
  };

  const viewStub = async (tmId) => {
    try {
      const params = new URLSearchParams({ team_member_id: tmId, pay_run_id: run.id });
      const res = await axios.get(`${api}/payroll/pay-stub?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch (_e) {
      toast('Failed to load pay stub', 'error');
    }
  };

  const emailStub = async (m) => {
    if (!m.has_email) { toast('No email on file for this member', 'info'); return; }
    if (!window.confirm(`Email pay stub to ${m.team_member_email}?`)) return;
    setSendingId(m.team_member_id);
    try {
      await axios.post(`${api}/payroll/pay-stub/email`, {
        team_member_id: m.team_member_id,
        pay_run_id: run.id,
      });
      toast(`Pay stub emailed to ${m.team_member_email}`, 'success');
      await refreshMembers();
    } catch (err) {
      toast(err?.response?.data?.detail || 'Failed to email pay stub', 'error');
    }
    setSendingId(null);
  };

  const bulkEmailStubs = async () => {
    const eligible = (members || []).filter(m => m.has_email);
    if (eligible.length === 0) {
      toast('No team members in this run have an email on file', 'info');
      return;
    }
    if (!window.confirm(`Email pay stubs to ${eligible.length} team member${eligible.length === 1 ? '' : 's'}?`)) return;
    setBulkSending(true);
    try {
      const res = await axios.post(`${api}/payroll/pay-runs/${run.id}/email-stubs`);
      const { sent_count, skipped_count } = res.data || {};
      const summary = skipped_count
        ? `Emailed ${sent_count} of ${sent_count + skipped_count} — ${skipped_count} skipped`
        : `Emailed ${sent_count} pay stub${sent_count === 1 ? '' : 's'}`;
      toast(summary, sent_count > 0 ? 'success' : 'info');
      await refreshMembers();
    } catch (err) {
      toast(err?.response?.data?.detail || 'Failed to send pay stubs', 'error');
    }
    setBulkSending(false);
  };

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden" data-testid={`pay-run-row-${run.id}`}>
      <div className="flex items-center justify-between gap-3 py-2 px-3 hover:bg-gray-50 cursor-pointer" onClick={toggle}>
        <div className="min-w-0 flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {moment(run.created_at).format('MMM D, YYYY h:mm A')} • {run.line_count} line{run.line_count === 1 ? '' : 's'}
            </p>
            {run.note && <p className="text-xs text-gray-500 truncate">{run.note}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-semibold text-gray-900">{formatMoney(run.total_amount)}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onReverse(); }}
            className="text-xs text-red-600 hover:text-red-800"
            data-testid={`reverse-run-${run.id}`}
          >
            Reverse
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-3 py-2" data-testid={`pay-run-members-${run.id}`}>
          {loading ? (
            <p className="text-xs text-gray-500">Loading members…</p>
          ) : (members || []).length === 0 ? (
            <p className="text-xs text-gray-500">No team members in this run.</p>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Pay Stubs</p>
                {(members || []).some(m => m.has_email) && (
                  <button
                    onClick={bulkEmailStubs}
                    disabled={bulkSending}
                    className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 flex items-center gap-1.5"
                    data-testid={`bulk-email-stubs-${run.id}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    {bulkSending ? 'Sending…' : `Email All Stubs (${(members || []).filter(m => m.has_email).length})`}
                  </button>
                )}
              </div>
              {members.map(m => (
                <div key={m.team_member_id} className="flex items-center justify-between gap-2 py-1.5 px-2 bg-white rounded border border-gray-100">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{m.team_member_name}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {m.line_count} line{m.line_count === 1 ? '' : 's'} • {formatMoney(m.total_amount)}
                      {m.team_member_email && <span> • {m.team_member_email}</span>}
                    </p>
                    {m.last_sent_at && (
                      <p className="text-[10px] text-green-700 mt-0.5 flex items-center gap-1" title={moment(m.last_sent_at).format('MMM D, YYYY h:mm A')}>
                        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                        Sent {moment(m.last_sent_at).fromNow()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => viewStub(m.team_member_id)}
                      className="px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 rounded"
                      data-testid={`view-stub-${run.id}-${m.team_member_id}`}
                    >
                      View
                    </button>
                    <button
                      onClick={() => emailStub(m)}
                      disabled={!m.has_email || sendingId === m.team_member_id}
                      title={m.has_email ? `Email to ${m.team_member_email}` : 'No email on file'}
                      className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`email-stub-${run.id}-${m.team_member_id}`}
                    >
                      {sendingId === m.team_member_id ? 'Sending…' : 'Email'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Drawer = ({ title, onClose, children, testId, widthClass = "max-w-xl" }) => (
  <div className="fixed inset-0 z-50" data-testid={testId}>
    <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={onClose} />
    <div className={`absolute top-0 right-0 h-full w-full ${widthClass} bg-white shadow-2xl flex flex-col animate-slide-in-right`}>
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="drawer-close">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="p-5 overflow-y-auto flex-1">{children}</div>
    </div>
  </div>
);

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} data-testid={testId}>
    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="modal-close">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="p-5 overflow-y-auto">{children}</div>
    </div>
  </div>
);

export default PayrollPage;
